/**
 * Authentication Adapter
 *
 * Provides framework-agnostic authentication utilities that work across
 * different runtime environments. Uses mock auth for development/testing.
 */

export interface User {
  id: string;
  email?: string;
  name?: string;
  image?: string;
  role?: string;
}

export interface AuthSession {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AuthAdapter {
  getSession: () => Promise<AuthSession>;
  signIn: (email?: string, password?: string) => Promise<void>;
  signOut: () => Promise<void>;
  requireAuth: () => Promise<User>;
}

/**
 * Creates an auth adapter (mock implementation for development/testing)
 */
export const createAuth = (): AuthAdapter => ({
  getSession: async () => ({
    user: {
      id: "test-user",
      email: "test@example.com",
      name: "Test User",
      role: "user",
    },
    isAuthenticated: true,
    isLoading: false,
  }),
  signIn: async () => {
    console.warn("Mock sign in - not implemented");
  },
  signOut: async () => {
    console.warn("Mock sign out - not implemented");
  },
  requireAuth: async () => ({
    id: "test-user",
    email: "test@example.com",
    name: "Test User",
    role: "user",
  }),
});

// Singleton instance for stable reference across renders
const authInstance = createAuth();

/**
 * Hook-like interface for authentication (React-compatible)
 * Returns a stable reference to avoid useEffect dependency issues
 */
export const useAuth = () => {
  return authInstance;
};
